import { useDispatch, useSelector } from 'react-redux';
import { Vehicle } from '../../types/vehicle';
import styled from 'styled-components';
import { User } from '../../types/user';
import { getHighestBid, getMinBitAmount, getVehicleTimer } from '../../utils/vehicle';
import { NotesSection } from '../Notes';
import { VEHICLE_STATUSES } from '../../constants';
import FormattedAmount from '../FormattedAmount';
import Timer from '../Timer';
import { InfoValue, Title, InfoWrapper } from './styled';
import { CurrentBidComponent } from '../Typography';
import { push } from 'connected-react-router';
import { initVehicleDetailsData } from '../../actions/vehicles';
import { useParams } from 'react-router-dom';
import { ReservePriceLabel } from '../ReservePriceMet';
import { NotesIcon } from '../../pages/Admin/ViewVehicle/icons/NotesIcon';
import { Form } from 'react-final-form';
import { usePlaceSidePanelBidMutation } from '../../services/api/my-bids';
import { IncreaseAmountMobile } from '../IncreaseAmount/mobile';
import { RoundedBidInfoLabel } from '../../pages/Buyer/BidsList/components/tableBlocks/RoundedBidInfoLabel';
import { useIsAlternativeAuction } from '../../hooks';
import { AuctionLabel } from './AuctionLabel';
import { BidInfoLabel } from '../../pages/Buyer/BidsList/components/tableBlocks/BidInfoLabel';
import { IncreaseHighBid } from '../../pages/Buyer/BidsList/containers';

type Props = { vehicle: Vehicle };

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 12px;
  gap: 8px;
  position: relative;
  background: rgba(249, 249, 249, 1);
  border-radius: 15px;
  padding: 4px;
`;

const VehicleImage = styled.div<{ imageUrl: string }>`
  background-size: cover;
  background-repeat: no-repeat;
  min-height: 120px;
  border-radius: 20px;
  border: 0.5px solid rgba(210, 210, 210, 1)
  cursor: pointer;
  background-image: ${({ imageUrl }) => `url(${imageUrl})`};
`;

const VehicleInfoLabelWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const InfoLabel = styled.div`
  color: #757575;
  font-size: 10px;
  font-weight: 400;
`;

const BidInfoLabelWrapper = styled.div`
  position: absolute;
  right: -8px;
  top: -8px;
`;

const NotesWrapper = styled.div`
  background-color: rgba(57, 148, 222, 0.1);
  color: rgba(57, 148, 222, 1);
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
  display: flex;
  height: 26px;
  flex: 1 1 100%;
  padding: 1px 0;
  justify-content: center;
  align-items: center;

  & * .notes-container {
    font-size: 10px;

    background: transparent;
    border: none;
  }

  & * .add-notes-container {
    font-size: 10px;

    background: transparent;
    border: none;
  }

  & * .notes-text {
    max-width: 50px;
  }
`;

const NotesAndReserveWrapper = styled.div`
  display: flex;
  gap: 4px;
  & > div {
    flex: 1 1 100%;
  }
`
const AuctionLabelWrapper = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
`;

export const VehicleSideCard = ({ vehicle }: Props) => {
  const dispatch = useDispatch() as any;
  const { vehicleId } = useParams<{ vehicleId?: string }>();

  const isAlternativeAuction = useIsAlternativeAuction(vehicle);

  const user: User = useSelector((state: any) => state.user?.user);

  const highestBid = getHighestBid(vehicle);
  const myBid = vehicle.my_bid;
  const vehicleTimer = getVehicleTimer(vehicle);

  const minBidAmount = getMinBitAmount(vehicle, user);

  const vehicleImageSrc = vehicle.preview_image?.url ?? '';
  const vehicleName = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;

  const canPlaceBid = !!user && user.role === 'buyer' && vehicle.status === VEHICLE_STATUSES.ACTIVE;

  const handleOnClick = () => {
    if (vehicleId && Number(vehicleId) === vehicle.id) return;
    dispatch(push(`/vehicles/${vehicle.id}/view`));
    dispatch(initVehicleDetailsData(vehicle));
  };

  const VehicleValueInfo = () => (
    <VehicleInfoLabelWrapper>
      <InfoLabel>Book: </InfoLabel>
      <InfoValue size={10}>
        <FormattedAmount amount={vehicle.bluebook_value} />
      </InfoValue>
      <InfoLabel>/ </InfoLabel>
      <InfoLabel>miles: </InfoLabel>
      <InfoValue size={10}>
        <FormattedAmount prefix="" amount={vehicle.bluebook_mileage} />
      </InfoValue>
    </VehicleInfoLabelWrapper>
  );

  const [myBidLabel, myBidAmount] = (() => {
    if (isAlternativeAuction) {
      return ['Your Offer:', vehicle.my_alternative_bid_offer?.amount];
    }

    return ['Your Bid:', myBid?.amount];
  })();

  return (
    <Container>
      {/* <RoundedBidInfoLabel item={vehicle} /> */}
      <BidInfoLabelWrapper>
        <BidInfoLabel id={vehicle.id} item={vehicle} labelType={'right'} />
      </BidInfoLabelWrapper>
      <AuctionLabelWrapper>
        <AuctionLabel vehicle={vehicle} />
      </AuctionLabelWrapper>
      <VehicleImage imageUrl={vehicleImageSrc} onClick={handleOnClick} />
      <Title>{vehicleName}</Title>
      <VehicleValueInfo />
      <InfoWrapper>
        <Title>Current bid:</Title>
        <InfoValue>{myBid?.amount ? <CurrentBidComponent amount={highestBid.amount} /> : '-'}</InfoValue>
      </InfoWrapper>
      <InfoWrapper>
        <InfoLabel>Time left:</InfoLabel>
        <InfoValue black>
          <Timer toDate={vehicleTimer} />
        </InfoValue>
      </InfoWrapper>
      <InfoWrapper>
        <InfoLabel>{myBidLabel}</InfoLabel>
        <InfoValue>{myBidAmount ? <FormattedAmount amount={myBidAmount} /> : '-'}</InfoValue>
      </InfoWrapper>
      <NotesSection vehicle={vehicle} />
      <ReservePriceLabel activeVehicle={vehicle} />
      {canPlaceBid && (
        <IncreaseHighBid
          form={`biddingForm_${vehicle.id}`}
          vehicleId={vehicle.id}
          minAmount={minBidAmount}
          isSidePanelBid
          variant={'default'}
          type={isAlternativeAuction ? 'alternative_offer' : 'bid'}
        />
      )}
    </Container>
  );
};

const PlaceBidForm = ({ minAmount, id }: { minAmount: number; id: number }) => {
  const [placeBid] = usePlaceSidePanelBidMutation();

  const onSubmit = (values: { amount: string }) => {
    placeBid({ vehicle_id: id, amount: Number(minAmount) });
  };

  return (
    <Form onSubmit={onSubmit} name={`place-side-bid-${id}`}>
      {() => <IncreaseAmountMobile type="bid" initialAmount={minAmount} />}
    </Form>
  );
};
